/**
 * Starter kit builder.
 *
 * Each case type is a separate Shopify Bundles product. Step 01 chooses which
 * bundle; every later step is one of that bundle's own options. Adding puts a
 * single bundle variant in the cart and Shopify Bundles expands it into the
 * case, refill and free travel bag. A refill plan covering several refills
 * adds a scent picker, so a year of refills can be a mix rather than six of
 * the same.
 *
 * Steps after the first are rendered here rather than in Liquid because the
 * option set changes with the chosen bundle, and rendering server-side would
 * cost a round trip per click.
 */
import { addToCart, CONFIRM_MS, flatten, kitGroupId } from 'cart-add';
import { define } from 'custom-elements';
import {
  escapeAttr,
  escapeHtml,
  money,
  patchScentPicker,
  planName,
  planPeriod,
  planTerms,
  refillsIn,
  scentPicker,
  sellingPlan,
  srcsetFor,
  tick,
} from 'product-shared';

class KitBuilder extends HTMLElement {
  connectedCallback() {
    const data = this.querySelector('[data-kit-data]');
    if (!data) return;

    try {
      const parsed = JSON.parse(data.textContent);
      this.bundles = parsed.bundles || [];
      this.refill = parsed.refill || null;
      this.optionImages = parsed.optionImages || {};
      this.optionSwatches = parsed.optionSwatches || {};
      this.ball = parsed.ball || null;
      this.bestsellers = parsed.bestsellers || [];
    } catch {
      return;
    }

    if (this.bundles.length === 0) return;

    this.bundleIndex = 0;
    this.choices = [];
    this.planId = null;
    // "Just the kit" starts ticked: the kit is buyable as it stands, and a
    // plan is something the customer opts into.
    this.planChosen = true;
    this.scentCounts = {};
    // No ball until the customer picks one: the add-on is opt-in.
    this.ballId = null;

    this.el = {
      bundles: this.querySelector('[data-kit-bundles]'),
      bundleStep: this.querySelector('[data-kit-bundle-step]'),
      options: this.querySelector('[data-kit-options]'),
      rhythm: this.querySelector('[data-kit-rhythm]'),
      rhythmStep: this.querySelector('[data-kit-rhythm-step]'),
      rhythmNumber: this.querySelector('[data-kit-rhythm-number]'),
      terms: this.querySelector('[data-kit-terms]'),
      ball: this.querySelector('[data-kit-ball]'),
      ballStep: this.querySelector('[data-kit-ball-step]'),
      ballNumber: this.querySelector('[data-kit-ball-number]'),
      total: this.querySelector('[data-kit-total]'),
      add: this.querySelector('[data-kit-add]'),
      addLabel: this.querySelector('[data-kit-add-label] [data-roll-label]'),
      addError: this.querySelector('[data-add-error]'),
      image: this.querySelector('[data-kit-image]'),
      quantity: this.querySelector('[data-kit-quantity]'),
    };

    // The section's words, from the hidden tag Liquid writes just before the
    // builder. Read once here rather than looked up again on every redraw.
    this.strings = document.querySelector('[data-kit-strings]')?.dataset || {};

    if (this.el.addLabel) {
      const copy = this.el.addLabel.querySelector('.text-roll__text') || this.el.addLabel;
      this.el.addLabel.dataset.addLabel = copy.textContent.trim();
    }

    this.addEventListener('change', (event) => this.onChange(event));
    this.addEventListener('click', (event) => this.onScentStep(event));
    this.addEventListener('click', (event) => this.onIngredients(event));
    this.el.add?.addEventListener('click', () => this.add());

    this.resetChoices();
    this.renderAll();

    // On a case page the kit follows the colour picked on the case, so a
    // customer who arrived on the black case and switches to the starter kit
    // still has black, not the first colour.
    this.followCaseColour();
    document.addEventListener('change', (event) => {
      if (!this.contains(event.target) && event.target.closest('.product__form')) this.followCaseColour();
    });
  }

  /**
   * Picks the kit's case colour to match the case's own picker, by clicking the
   * kit's matching swatch so the image, ball cards and price follow as they do
   * for a customer's click. Nothing happens when the kit has no such colour.
   */
  followCaseColour() {
    const values = new Set(this.bundle.variants.map((variant) => flatten(variant.options[0])));
    const picked = [...document.querySelectorAll('.product__form input[type="radio"]:checked')].find(
      (input) => !this.contains(input) && values.has(flatten(input.value))
    );
    if (!picked || flatten(picked.value) === flatten(this.choices[0] || '')) return;

    const match = [...this.querySelectorAll('input[name="kit-option-0"]')].find(
      (input) => !input.disabled && flatten(input.value) === flatten(picked.value)
    );
    if (!match) return;

    match.checked = true;
    match.dispatchEvent(new Event('change', { bubbles: true }));
  }

  get bundle() {
    return this.bundles[this.bundleIndex];
  }

  /**
   * Choosing the kit is only a step when there is more than one to choose from.
   * With one kit the step is hidden and everything below it moves up a number,
   * so the customer counts 01, 02, 03 rather than starting at 02.
   */
  get firstStep() {
    return this.bundles.length > 1 ? 2 : 1;
  }

  /** The variant matching every current option choice. */
  get variant() {
    return this.bundle.variants.find((variant) =>
      variant.options.every((option, index) => option === this.choices[index])
    );
  }

  /**
   * The refill variant used for the repeat-delivery line. Matched to whichever
   * scent the customer picked in the bundle, so the subscription continues the
   * scent they chose rather than an unrelated one. Falls back to the first
   * subscribable variant when no name matches.
   */
  get refillVariant() {
    if (!this.refill?.variants.length) return null;

    const chosen = this.choices.map(flatten);

    return (
      this.refill.variants.find((variant) => chosen.includes(flatten(variant.title))) ||
      this.refill.variants[0]
    );
  }

  /** The component product's photo for a given option value, if there is one. */
  imageFor(value) {
    return this.optionImages[flatten(value)] || null;
  }

  /** The component variant's swatch colour for an option value, if it has one. */
  swatchFor(value) {
    const colour = this.optionSwatches[flatten(value)];
    return /^#[0-9a-f]{3,8}$/i.test(colour || '') ? colour : null;
  }

  /* ------------------------------------------------------------------ ball */

  get ballVariant() {
    if (!this.ballId) return null;
    return (this.ball?.variants || []).find((variant) => variant.id === this.ballId) || null;
  }

  /**
   * The case colour the ball is being paired with. Option 0 is the bundle's
   * case option, which is a colour name rather than a variant — the bundle
   * carries no case variant of its own.
   */
  get caseValue() {
    return this.choices[0] || null;
  }

  /**
   * The supplied photograph of the chosen case and ball together, when the
   * client has shot that pairing. Pairings are sparse on purpose, so a missing
   * one means "keep the case photo", never an error.
   */
  get pairingImage() {
    return this.pairingFor(this.ballVariant);
  }

  pairingFor(ball) {
    if (!ball || !this.caseValue) return null;

    return this.ball.pairings?.[`${flatten(this.caseValue)}|${flatten(ball.title)}`] || null;
  }

  /**
   * Which of the bundle's options is the scent: the one whose values are the
   * refill product's variants. That step is picked with quantity tiles rather
   * than one-of cards, because a plan can cover several refills.
   */
  get scentOptionIndex() {
    const titles = (this.refill?.variants || []).map((variant) => flatten(variant.title));
    if (!titles.length) return -1;

    return this.bundle.options.findIndex((option, index) =>
      this.bundle.variants.some((variant) => titles.includes(flatten(variant.options[index])))
    );
  }

  /** The kit itself: every option but the scent, which is picked last. */
  get caseChosen() {
    const scentIndex = this.scentOptionIndex;
    return this.bundle.options.every((option, index) => index === scentIndex || this.choices[index]);
  }

  /** Scents to choose: one for the kit, or one per refill the plan covers. */
  get scentsNeeded() {
    if (this.scentOptionIndex < 0) return 0;
    return this.plan ? refillsIn(this.plan) : 1;
  }

  get scentsChosen() {
    return Object.values(this.scentCounts).reduce((sum, n) => sum + n, 0);
  }

  get scentsReady() {
    if (this.scentsNeeded === 0) return true;
    // Bought once, the kit needs its one refill and can take more besides.
    return this.plan ? this.scentsChosen === this.scentsNeeded : this.scentsChosen >= 1;
  }

  /**
   * Refills bought once on top of the kit's own: every scent picked beyond the
   * first, which the bundle carries. Only without a plan, since a plan's
   * scents are its deliveries rather than extras.
   */
  get extraRefills() {
    if (this.plan || this.scentOptionIndex < 0) return [];

    const kitScent = Object.keys(this.scentCounts)[0];
    return Object.entries(this.scentCounts)
      .map(([id, count]) => ({ id: Number(id), count: id === kitScent ? count - 1 : count }))
      .filter(({ count }) => count > 0);
  }

  get extraRefillsPrice() {
    const variants = this.refill?.variants || [];
    return this.extraRefills.reduce(
      (sum, { id, count }) => sum + (variants.find((v) => v.id === id)?.price ?? 0) * count,
      0
    );
  }

  get plan() {
    return this.refillVariant?.plans.find((plan) => plan.id === this.planId) || null;
  }

  /** Default to the first available variant so the kit opens in a buyable state. */
  resetChoices() {
    const opening =
      this.bundle.variants.find((variant) => variant.available) || this.bundle.variants[0];

    this.choices = [...(opening?.options || [])];
    this.planId = null;
    this.planChosen = true;
    // The scent is the customer's to pick: the tiles open empty.
    this.clearScents();
  }

  onChange(event) {
    const { name, value } = event.target;

    if (name === 'kit-bundle') {
      this.bundleIndex = Number(value);
      // A different bundle has a different option set, so the previous
      // selections cannot be carried across.
      this.resetChoices();
      this.renderAll();
      return;
    }

    if (name.startsWith('kit-option-')) {
      // The plan is chosen before the scent, so it survives a scent change: the
      // same plan on the new scent's variant is a different id with the same
      // name, and only a scent that doesn't offer it drops the choice.
      const chosenPlan = this.plan?.name;
      this.choices[Number(name.replace('kit-option-', ''))] = value;
      const named = this.querySelector(`[data-kit-step-value="${name.replace('kit-option-', '')}"]`);
      if (named) named.textContent = value;
      const samePlan = this.refillVariant?.plans.find((plan) => plan.name === chosenPlan);
      this.planId = samePlan ? samePlan.id : null;
      if (chosenPlan && !samePlan) this.planChosen = false;
      this.refreshScents();
      this.renderRhythm();
      // The ball cards show each ball in the chosen case, so a new case
      // colour redraws them.
      if (name === 'kit-option-0') this.renderBall();
      this.updateImage();
      this.updateTotal();
      return;
    }

    if (name === 'kit-ball') {
      // "" is the "no ball" card, which is how the step opens.
      this.ballId = value ? Number(value) : null;
      // The cards read their own selected state from the radio, so only the
      // photo and the price need to follow.
      this.updateImage();
      this.updateTotal();
      return;
    }

    if (name === 'kit-rhythm') {
      this.planId = value ? Number(value) : null;
      this.planChosen = true;
      // A different plan covers a different number of refills, so the set
      // chosen for the last one cannot carry over.
      // The radio the customer clicked is already checked, and the cards read
      // their selected state from it, so the list itself needs no redraw.
      this.clearScents();
      this.refreshScents();
      this.updateTotal();
      this.renderTerms();
    }
  }

  /** Nothing chosen: the tiles start empty rather than guessing a scent. */
  clearScents() {
    this.scentCounts = {};
    if (this.scentOptionIndex >= 0) this.choices[this.scentOptionIndex] = null;
  }

  /**
   * The kit's own refill takes the first scent picked, so the bundle variant
   * still resolves while the rest of the set is being chosen.
   */
  syncScentChoice() {
    const index = this.scentOptionIndex;
    if (index < 0) return;

    const first = Object.keys(this.scentCounts)[0];
    const variant = (this.refill?.variants || []).find((item) => String(item.id) === first);
    this.choices[index] = variant ? this.valueForScent(variant.title, index) : null;
  }

  /** The bundle's own spelling of a scent, which its variants are matched on. */
  valueForScent(title, index) {
    const values = this.bundle.variants.map((variant) => variant.options[index]);
    return values.find((value) => flatten(value) === flatten(title)) || title;
  }

  onScentStep(event) {
    const step = event.target.closest('[data-kit-scent-step]');
    if (!step) return;

    const id = step.dataset.variant;
    const delta = Number(step.dataset.kitScentStep);
    const next = (this.scentCounts[id] || 0) + delta;

    if (next < 0) return;
    if (delta > 0 && this.plan && this.scentsChosen >= this.scentsNeeded) return;

    if (next === 0) delete this.scentCounts[id];
    else this.scentCounts[id] = next;

    this.syncScentChoice();
    this.refreshScents();
    this.updateImage();
    this.updateTotal();
  }

  /**
   * Redraws the scent tiles and nothing else.
   *
   * Rebuilding every step would recreate each card's photo, and a browser
   * fetching and painting those again is the flicker a customer sees when they
   * step a quantity or change plan. Only the tiles have changed, so only the
   * tiles are replaced; the other steps keep their images and their DOM.
   */
  refreshScents() {
    const picker = this.querySelector('[data-kit-scent-picker]');
    if (!picker) return;

    // The tiles on the page are patched where they can be; a different set
    // (a new plan needing a different number of them) is drawn afresh.
    const patched = patchScentPicker(picker, {
      scents: this.scents(),
      needed: this.scentCap,
      strings: this.strings,
    });

    if (!patched) picker.innerHTML = this.scentTiles(this.scentOptionIndex);
    this.renderExtras();
    this.updateAvailability();
  }

  /**
   * Which values can still be reached, without redrawing the cards. The scent
   * a customer picks can close off a colour, and saying so is a `disabled`
   * attribute rather than a reason to rebuild the step.
   */
  updateAvailability() {
    const scentIndex = this.scentOptionIndex;

    this.bundle.options.forEach((option, optionIndex) => {
      if (optionIndex === scentIndex) return;

      this.querySelectorAll(`input[name="kit-option-${optionIndex}"]`).forEach((input) => {
        input.disabled = !this.bundle.variants.some(
          (variant) => variant.available && variant.options[optionIndex] === input.value
        );
      });
    });
  }

  renderAll() {
    this.renderBundles();
    this.renderOptions();
    this.renderExtras();
    this.renderRhythm();
    this.renderBall();
    this.updateImage();
    this.updateTotal();
  }

  /**
   * The add-on ball, asked last: the kit is a complete purchase without it.
   *
   * Each card shows its ball in the chosen case when that pairing was shot,
   * else the ball's own photo. On the product page the hero is hidden in
   * favour of the product gallery, so the cards are the only place the
   * pairing photograph appears.
   */
  renderBall() {
    const step = this.el.ballStep;
    if (!step) return;

    const variants = (this.ball?.variants || []).filter((variant) => variant.available);

    step.hidden = variants.length === 0;
    if (step.hidden) {
      this.ballId = null;
      if (this.el.ball) this.el.ball.innerHTML = '';
      return;
    }

    if (this.el.ballNumber) {
      this.el.ballNumber.textContent = this.firstStep + this.bundle.options.length + 1;
    }

    this.el.ball.innerHTML = [
      this.card({
        name: 'kit-ball',
        value: '',
        index: 'none',
        checked: this.ballId === null,
        title: this.strings.noBall || 'No ball',
        placeholder: true,
      }),
      ...variants.map((variant, index) =>
        this.card({
          name: 'kit-ball',
          value: variant.id,
          index,
          checked: this.ballId === variant.id,
          image: this.pairingFor(variant) || variant.image,
          title: variant.title,
          meta: money(variant.price),
        })
      ),
    ].join('');
  }

  card({ name, value, index, checked, image, title, meta, disabled, placeholder, badge, swatch }) {
    const id = `${name}-${index}`;
    // A card with nothing to show — "no ball" — still fills the square its
    // neighbours' photos fill, so the row keeps one rhythm instead of one
    // short box beside a row of tall ones. A swatch draws its colour instead
    // of a photo, and its name stays only for screen readers, as on the case
    // picker: the chosen value is named once under the row.
    const media = swatch
      ? `<span class="variant-option__media variant-option__media--colour" style="background: ${swatch}"></span>`
      : image
        ? `<img class="variant-option__media" src="${escapeAttr(image)}" alt="" loading="lazy" width="140" height="140">`
        : placeholder
          ? '<span class="variant-option__media variant-option__media--colour"></span>'
          : '';

    // The corner tick is drawn at icon-check.svg's lighter weight, the one the
    // product page's own picker uses, since on a case page the two sit one
    // above the other.
    return `
      <div class="variant-option">
        <input class="variant-option__input visually-hidden" type="radio"
               id="${id}" name="${name}" value="${escapeAttr(value)}"
               ${checked ? 'checked' : ''} ${disabled ? 'disabled' : ''}>
        <label class="variant-option__label" for="${id}">
          ${media}
          <span class="variant-option__body${swatch ? ' visually-hidden' : ''}">
            <span class="variant-option__name">${escapeHtml(title)}</span>
            ${meta ? `<span class="variant-option__price">${meta}</span>` : ''}
            ${badge ? `<span class="variant-option__badge">${escapeHtml(badge)}</span>` : ''}
          </span>
          <span class="variant-option__tick">${tick(14, 2)}</span>
        </label>
      </div>`;
  }

  renderBundles() {
    if (this.el.bundleStep) this.el.bundleStep.hidden = this.bundles.length < 2;

    // Named by the case, like the plan cards below: the kit's contents are the
    // same either way, so the case and its colour range are the whole choice.
    this.el.bundles.innerHTML = this.bundles
      .map((bundle, index) => {
        const price = money(bundle.price);
        const colours = bundle.colours > 1
          ? (this.strings.bundleColours || '').replace('{{ count }}', bundle.colours)
          : '';

        return sellingPlan({
          name: bundle.caseName || bundle.title,
          inputName: 'kit-bundle',
          value: index,
          checked: index === this.bundleIndex,
          total: bundle.price,
          note: [
            bundle.priceVaries ? (this.strings.bundleFrom || '').replace('{{ price }}', price) : price,
            colours,
          ]
            .filter(Boolean)
            .join(' · '),
        });
      })
      .join('');
  }

  /**
   * One fieldset per option on the chosen bundle. Values that cannot be reached
   * from the current selection are dimmed, so an unavailable colour reads as
   * unavailable before it is clicked.
   */
  renderOptions() {
    const scentIndex = this.scentOptionIndex;

    this.el.options.innerHTML = this.bundle.options
      .map((option, optionIndex) => {
        const legend = `
          <legend class="variant-step__legend">
            <span class="variant-step__number">${this.numberForOption(optionIndex)}</span>
            <span>/ ${escapeHtml(this.labelForOption(option, optionIndex, scentIndex))}</span>
          </legend>`;

        if (optionIndex === scentIndex) {
          return `
            <fieldset class="variant-step">
              ${legend}
              <div class="scent-rows" data-kit-scent-picker>${this.scentTiles(optionIndex)}</div>
              <p class="kit__extras" data-kit-extras aria-live="polite" hidden></p>
              ${this.ingredientsLink()}
            </fieldset>`;
        }

        const values = [...new Set(this.bundle.variants.map((v) => v.options[optionIndex]))];

        // Swatches only when the case picker shows them and every value has a
        // colour to draw; otherwise the step keeps its photo cards.
        let style = mirroredStyle(optionIndex);
        const swatches = style.includes('variant-step--swatch') && values.every((value) => this.swatchFor(value));
        if (!swatches) style = style.replace(' variant-step--swatch', '');

        const cards = values
          .map((value, valueIndex) => {
            const candidate = [...this.choices];
            candidate[optionIndex] = value;

            const match = this.bundle.variants.find((variant) =>
              variant.options.every((opt, i) => opt === candidate[i])
            );
            const reachable = this.bundle.variants.some(
              (variant) => variant.available && variant.options[optionIndex] === value
            );

            return this.card({
              name: `kit-option-${optionIndex}`,
              value,
              index: `${optionIndex}-${valueIndex}`,
              checked: this.choices[optionIndex] === value,
              // Prefer the component product's photo of this exact colour or
              // scent. Bundle variants carry no images of their own, so falling
              // back to one would show the same generic shot on every card.
              image: this.imageFor(value) || match?.image,
              title: value,
              disabled: !reachable,
              badge: swatches ? '' : this.bestsellerLabel(value),
              swatch: swatches ? this.swatchFor(value) : null,
            });
          })
          .join('');

        const named = swatches
          ? `<p class="variant-step__value" data-kit-step-value="${optionIndex}">${escapeHtml(this.choices[optionIndex] || '')}</p>`
          : '';

        return `
          <fieldset class="variant-step${style}">
            ${legend}
            <div class="variant-step__options">${cards}</div>
            ${named}
          </fieldset>`;
      })
      .join('');

    // The plan follows the first option (the case), so it lands between the
    // option steps rather than after them.
    if (this.el.rhythmNumber) this.el.rhythmNumber.textContent = this.firstStep + 1;

    this.placeRhythmStep();
  }

  /**
   * What a step is called. The bundle's own option is "CASE", whose values are
   * colours, so the steps are named for what is actually being chosen. Any
   * further option falls back to its own name, lowercased and prefixed, since
   * admin spells them in caps.
   */
  labelForOption(option, optionIndex, scentIndex) {
    if (optionIndex === scentIndex) return this.strings.chooseScent || option.name;
    if (optionIndex === 0) return this.strings.chooseColour || option.name;

    return `${this.strings.stepLabel || ''} ${option.name.toLowerCase()}`.trim();
  }

  /**
   * Where a step's number lands. The case comes first, then the refill plan,
   * then the rest of the options — choosing how often refills arrive makes more
   * sense before choosing the scent they come in.
   */
  numberForOption(optionIndex) {
    return optionIndex === 0 ? this.firstStep : this.firstStep + 1 + optionIndex;
  }

  /**
   * The plan's markup is written once in Liquid, after the options; with more
   * than one option it belongs between the first and the second, so it moves.
   */
  placeRhythmStep() {
    const step = this.el.rhythmStep;
    const first = this.el.options?.firstElementChild;
    if (!step || !first || this.bundle.options.length < 2) return;
    if (first.nextElementSibling !== step) first.after(step);
  }

  /**
   * Ongoing refill deliveries. The bundle already contains the first refill, so
   * this step is about what happens afterwards, and declining it is the
   * default rather than an afterthought.
   */
  renderRhythm() {
    const plans = this.refillVariant?.plans || [];

    this.el.rhythmStep.hidden = plans.length === 0;
    if (plans.length === 0) {
      this.el.rhythm.innerHTML = '';
      this.renderTerms();
      return;
    }

    this.el.rhythm.innerHTML = [
      sellingPlan({
        name: this.strings.kitOnly || 'Just the kit',
        inputName: 'kit-rhythm',
        value: '',
        checked: this.planChosen && this.planId === null,
        total: 0,
        note: this.strings.kitOnlyNote || '',
      }),
      ...plans.map((plan) => {
        const count = refillsIn(plan);
        return sellingPlan({
          name: planName(plan.name),
          inputName: 'kit-rhythm',
          value: plan.id,
          checked: this.planId === plan.id,
          total: plan.price * count,
          per: (this.strings.planEvery || '').replace('{{ period }}', planPeriod(plan.name)),
          // What the same refills cost bought one at a time, which is the
          // saving the plan is actually offering.
          rrp: (this.refillVariant?.price ?? 0) * count,
          savingLabel: this.strings.savePercent || '',
          // Set when the section shows the saving in money rather than as a
          // percentage; it is worked out from the same two prices either way.
          savingAmountLabel: this.strings.saveAmount || '',
        });
      }),
    ].join('');
    this.renderTerms();
  }

  /** The chosen plan's renewal terms under the cards; hidden for the kit alone. */
  renderTerms() {
    const terms = this.el.terms;
    if (!terms) return;

    const plan = (this.refillVariant?.plans || []).find((item) => item.id === this.planId);
    const markup = planTerms({
      plan,
      total: plan ? plan.price * refillsIn(plan) : 0,
      label: this.strings.planTermsLabel,
      text: this.strings.planTerms,
    });

    terms.innerHTML = markup;
    terms.hidden = !markup;
  }

  /**
   * The scent step: the buying journey's tiles, laid out as rows, with a
   * quantity on each. Nothing is chosen for the customer — a plan covering six
   * refills asks for six, and a kit on its own asks for one.
   */
  /** The scents as the picker wants them, with what the bundle can be built in. */
  scents(optionIndex = this.scentOptionIndex) {
    return (this.refill?.variants || []).map((variant) => {
      const value = this.valueForScent(variant.title, optionIndex);

      return {
        id: variant.id,
        title: variant.title,
        notes: variant.notes || '',
        quantity: this.scentCounts[variant.id] || 0,
        // The component product's own photo of this scent first: bundle
        // variants carry no images, and the refill's may be missing too.
        image: this.imageFor(value) || this.imageFor(variant.title) || variant.image,
        // The bundle decides what can be bought, so a scent the kit cannot be
        // built in is closed off here even when the refill itself is in stock.
        available: this.bundle.variants.some(
          (item) => item.available && flatten(item.options[optionIndex]) === flatten(value)
        ),
      };
    });
  }

  /**
   * The bestseller badge for an option value, or nothing. The values come
   * from the section setting and the component variants' own metafield, so
   * no colour is marked until the merchant says which one sells best.
   */
  bestsellerLabel(value) {
    const label = this.strings.bestseller || '';
    return label && this.bestsellers.includes(flatten(value)) ? label : '';
  }

  /**
   * A quiet link under the scents to the ingredients panel further down the
   * builder, for anyone checking a scent against a sensitivity. Only drawn when
   * the section has an ingredients panel to point at.
   */
  ingredientsLink() {
    if (!this.strings.ingredientsTarget || !this.strings.viewIngredients) return '';

    return `
      <p class="kit__ingredients">
        <a class="link" href="#${escapeAttr(this.strings.ingredientsTarget)}" data-kit-ingredients>${escapeHtml(this.strings.viewIngredients)}</a>
      </p>`;
  }

  /** Opens the panel before scrolling to it, so the list is what lands in view. */
  onIngredients(event) {
    const link = event.target.closest('[data-kit-ingredients]');
    if (!link) return;

    const panel = document.getElementById(link.hash.slice(1));
    if (!panel) return;

    event.preventDefault();
    panel.open = true;
    panel.scrollIntoView({ behavior: 'smooth', block: 'start' });
    panel.querySelector('summary')?.focus({ preventScroll: true });
  }

  /** A plan asks for exactly its refills; bought once, the tiles take any number. */
  get scentCap() {
    return this.plan ? this.scentsNeeded : 0;
  }

  /**
   * The line under the tiles when buying once: the first refill is in the kit
   * and any more are charged as they are. Two or more extras show what the
   * cheapest-per-refill plan would save on them instead.
   */
  renderExtras() {
    const note = this.querySelector('[data-kit-extras]');
    if (!note) return;

    const each = this.refillVariant?.price ?? 0;
    note.hidden = Boolean(this.plan) || !each;
    if (note.hidden) return;

    const extras = this.extraRefills.reduce((sum, { count }) => sum + count, 0);
    const lines = [
      escapeHtml(this.strings.extrasNote || '').replace('{{ price }}', `<strong>${money(each)}</strong>`),
    ];

    if (extras > 0) {
      lines[0] = escapeHtml(this.strings.extrasAdded || '')
        .replace('{{ count }}', `<strong>${extras}</strong>`)
        .replace('{{ price }}', `<strong>${money(this.extraRefillsPrice)}</strong>`);
    }

    const best = Math.min(...(this.refillVariant?.plans || []).map((plan) => plan.price));
    const saving = Number.isFinite(best) ? (each - best) * extras : 0;
    if (extras >= 2 && saving > 0 && this.strings.extrasSubscribe) {
      lines.push(
        `<span class="kit__extras-nudge">${escapeHtml(this.strings.extrasSubscribe).replace('{{ amount }}', `<strong>${money(saving)}</strong>`)}</span>`
      );
    }

    note.innerHTML = lines.join(' ');
  }

  scentTiles(optionIndex) {
    return scentPicker({
      scents: this.scents(optionIndex),
      needed: this.scentCap,
      stepAttribute: 'data-kit-scent-step',
      id: 'kit-tally',
      strings: this.strings,
    });
  }

  /**
   * The main image follows the first option that has a component photo, which
   * in practice is the case colour. Bundle variants have no images, so without
   * this the hero would never change as the customer configures the kit.
   */
  updateImage() {
    const fromOption = this.choices.map((value) => this.imageFor(value)).find(Boolean);
    // A shot of the chosen case and ball together outranks the case's own: it
    // is the thing being bought. Without one the case photo stands, so an
    // unshot pairing is still perfectly buyable.
    const image = this.pairingImage || fromOption || this.variant?.image || this.bundle.image;

    if (image && this.el.image) {
      this.el.image.src = image;
      this.el.image.srcset = srcsetFor(image);
    }

    // On the product page this section's hero is hidden in favour of the
    // product gallery, so the gallery follows the kit instead: the pairing
    // photo laid over it when a ball is chosen, else the case colour's own
    // slide. Only while the kit is showing, since the case view's own picker
    // drives the gallery otherwise.
    const gallery = document.querySelector('product-media');
    if (!gallery || document.documentElement.dataset.purchaseMode !== 'kit') return;

    const pairing = this.pairingImage;
    if (pairing) {
      gallery.feature(pairing, srcsetFor(pairing));
    } else {
      gallery.feature(null);
      if (fromOption) gallery.showSource(fromOption);
    }
  }

  updateTotal() {
    const variant = this.variant;
    const quantity = Math.max(1, Number(this.el.quantity?.value) || 1);

    // The kit itself, plus the first charge of the refill plan when subscribing:
    // one refill, or one per scent chosen when the plan covers several.
    const refills = this.scentsNeeded === 0 ? 1 : this.scentsChosen;
    const unit =
      (variant?.price ?? 0) +
      (this.plan ? this.plan.price * refills : this.extraRefillsPrice) +
      // One ball per kit, so it scales with the quantity like the kit does.
      (this.ballVariant?.price ?? 0);

    if (this.el.total) this.el.total.textContent = money(unit * quantity);

    const buyable = Boolean(variant?.available);
    // A part-chosen kit is not one anyone can buy yet, so the button waits for
    // every step the label is still asking for.
    if (this.el.add) this.el.add.disabled = !buyable || !this.planChosen || !this.scentsReady;

    this.updateAddLabel(buyable);
  }

  /**
   * The button names the step still to do, so a disabled button says why. It
   * only reads "Add to cart" once the kit is complete.
   */
  updateAddLabel(buyable) {
    if (!this.el.addLabel) return;

    const waiting = [
      [!this.caseChosen, this.strings.pickCase],
      [!this.planChosen, this.strings.pickPlan],
      [!this.scentsReady, this.strings.pickScents],
    ].find(([unmet, label]) => unmet && label);

    const label = waiting ? waiting[1] : this.el.addLabel.dataset.addLabel;
    if (!label) return;

    // The label may have been split into two copies for the hover roll.
    const copies = this.el.addLabel.querySelectorAll('.text-roll__text');
    if (copies.length) copies.forEach((copy) => (copy.textContent = label));
    else this.el.addLabel.textContent = label;
  }

  /** Drives the button's label and mark. Disabled only while the request runs. */
  setAddState(state) {
    if (!this.el.add) return;
    this.el.add.dataset.state = state;
    this.el.add.disabled = state === 'adding';
  }

  async add() {
    const variant = this.variant;
    if (!variant || !this.planChosen || !this.scentsReady) return;

    const quantity = Math.max(1, Number(this.el.quantity?.value) || 1);

    // Groups the kit line with its subscription so the cart can treat them
    // as one purchase.
    const kitId = kitGroupId();

    // The bundle line. Shopify Bundles expands it into the case, the first
    // refill and the free travel bag at the bundle price.
    const items = [{ id: variant.id, quantity, properties: { _kit: kitId } }];

    // The add-on ball, when one was chosen. Its own line, never a component:
    // the bundle's contents are fixed and this is the part the customer opted
    // into. The shared `_kit` property keeps it grouped in the drawer.
    if (this.ballVariant) {
      items.push({ id: this.ballVariant.id, quantity, properties: { _kit: kitId } });
    }

    // Extra refills bought once alongside the kit, at the refill's own price.
    this.extraRefills.forEach(({ id, count }) => {
      items.push({ id, quantity: count * quantity, properties: { _kit: kitId } });
    });

    // Ongoing refills, only when the customer opted in. These are the only
    // lines that can carry a selling plan. A plan covering several refills adds
    // a line per scent chosen; one covering a single refill follows the kit's.
    if (this.planId) {
      const picked =
        this.scentsNeeded > 0
          ? Object.entries(this.scentCounts).map(([id, count]) => ({ id: Number(id), count }))
          : this.refillVariant
            ? [{ id: this.refillVariant.id, count: 1 }]
            : [];

      picked.forEach(({ id, count }) => {
        items.push({
          id,
          quantity: count * quantity,
          selling_plan: this.planId,
          properties: { _kit: kitId },
        });
      });
    }

    // The drawer waits for the response (addToCart opens it), so it never shows
    // the previous cart with the new lines still on their way.
    this.setAddState('adding');

    let added = false;
    try {
      added = await addToCart({ items, errorTarget: this.el.addError });
    } finally {
      this.setAddState(added ? 'added' : 'idle');
      if (added) setTimeout(() => this.setAddState('idle'), CONFIRM_MS);
      this.updateTotal();
    }
  }
}

/**
 * The style classes on the matching step of the case's own picker, when the
 * kit sits on a case page: chips per device, or swatches. Copying them keeps
 * the two views' colour steps alike without the kit needing settings of its
 * own (it has none left).
 */
function mirroredStyle(optionIndex) {
  // A kit page has no case picker to copy, so its case colours default to the
  // swatches the case pages use; the step still falls back to photo cards
  // when a colour has no swatch.
  if (!document.querySelector('.product variant-picker')) {
    return optionIndex === 0 ? ' variant-step--swatch' : '';
  }

  const step = document.querySelector(
    `.product variant-picker .variant-step[data-option-position="${optionIndex + 1}"]`
  );
  if (!step) return '';
  return [...step.classList]
    .filter((name) => name.startsWith('variant-step--chips') || name === 'variant-step--swatch')
    .map((name) => ` ${name}`)
    .join('');
}

define('kit-builder', KitBuilder);

// Quantity changes reprice the kit.
document.addEventListener('change', (event) => {
  if (event.target.matches('[data-kit-quantity]')) {
    event.target.closest('kit-builder')?.updateTotal();
  }
});
